import React, { useState, useEffect, useCallback } from "react";
import "./ExchangeRate.css";
import axiosInstance from "../../../Config/axiosInstance";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton } from "@mui/material";
import { format } from "date-fns";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";

import AddExchangeRate from "./AddExchangeRate";
import EditExchangeRate from "./EditExchangeRate";

const SYSTEM_CURRENCY = "EUR";

const ExchangeRate = () => {
  const [rows, setRows] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddPopupOpen, setAddPopupOpen] = useState(false);
  const [isEditPopupOpen, setEditPopupOpen] = useState(false);
  const [exchangeRateToEdit, setExchangeRateToEdit] = useState(null);

  // Stări pentru dialogul de ștergere
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedExchangeRateId, setSelectedExchangeRateId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Permisiuni
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // Normalizează URL-ul și returnează doar pathname-ul
  const getPathFromUrl = useCallback((url) => {
    try {
      const { pathname } = new URL(url);
      return pathname;
    } catch (error) {
      console.error("Eroare la extragerea URL-ului:", error);
      return url;
    }
  }, []);

  // Verifică permisiunile pentru pagina curentă
  useEffect(() => {
    const currentUrl = window.location.href;
    const url = getPathFromUrl(currentUrl);

    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    console.log("Verificare permisiuni pentru URL:", url);
    console.log("Permisiuni din localStorage:", permissions);

    const hasCreatePermission = permissions.some(
      (page) => getPathFromUrl(page.url) === url && page.can_create === true
    );
    setCanCreate(hasCreatePermission);
    console.log("Are permisiune de create:", hasCreatePermission);

    const hasEditPermission = permissions.some(
      (page) => getPathFromUrl(page.url) === url && page.can_edit === true
    );
    setCanEdit(hasEditPermission);
    console.log("Are permisiune de editare:", hasEditPermission);

    const hasDeletePermission = permissions.some(
      (page) => getPathFromUrl(page.url) === url && page.can_delete === true
    );
    setCanDelete(hasDeletePermission);
    console.log("Are permisiune de delete:", hasDeletePermission);
  }, [getPathFromUrl]);

  // Încarcă lista valutelor
  useEffect(() => {
    axiosInstance
      .get("/currencies/")
      .then((response) => {
        console.log("✅ Răspuns currencies:", response.data);
        setCurrencies(response.data);
      })
      .catch((error) => {
        console.error("❌ Eroare la încărcarea valutelor:", error);
        setError("Nu s-au putut încărca valutele");
      });
  }, []);

  // Formatează valoarea ratei de schimb
  const formatExchangeRate = (value) => {
    if (value === null || value === undefined || value === "") return "N/A";

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "N/A";

    return new Intl.NumberFormat("ro-RO", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 6,
    }).format(numValue);
  };

  // Încarcă și pregătește datele pentru grid
  const fetchExchangeRates = useCallback(() => {
    if (currencies.length === 0) return;

    setLoading(true);
    setError(null);

    axiosInstance
      .get("/exchange-rate/")
      .then((response) => {
        console.log("✅ Răspuns exchange-rates:", response.data);

        if (!Array.isArray(response.data)) {
          console.error("Datele primite nu sunt un array:", response.data);
          setError("Format de date invalid de la server");
          setRows([]);
          setLoading(false);
          return;
        }

        if (response.data.length === 0) {
          console.log("Nu există date în exchange-rates");
          setRows([]);
          setLoading(false);
          return;
        }

        let dataWithId = response.data.map((item, index) => ({
          ...item,
          id: item.id || index + 1,
          formattedDate: item.date
            ? format(new Date(item.date), "dd.MM.yyyy")
            : "N/A",
          formattedCreatedAt: item.created_at
            ? format(new Date(item.created_at), "dd.MM.yyyy HH:mm")
            : "N/A",
          rawValue: item.value,
        }));

        dataWithId = dataWithId.map((item) => {
          const currency = currencies.find((c) => c.id === item.currency);
          return {
            ...item,
            currency_name: currency ? currency.name : "N/A",
            currency_abbreviation: currency ? currency.abbreviation : "N/A",
            system_currency: SYSTEM_CURRENCY,
          };
        });

        // Sortare după created_at descrescător
        dataWithId = dataWithId.sort((a, b) => {
          const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
          const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
          return dateB - dateA;
        });

        dataWithId = dataWithId.map((item, index) => ({
          ...item,
          nr_crt: index + 1,
          formattedValue: formatExchangeRate(item.rawValue),
        }));

        console.log("✅ Date procesate final:", dataWithId);
        setRows(dataWithId);
        setLoading(false);
      })
      .catch((error) => {
        console.error("❌ Eroare la încărcarea ratelor de schimb:", error);
        setError(`Eroare la încărcare: ${error.message}`);
        setLoading(false);
      });
  }, [currencies]);

  useEffect(() => {
    fetchExchangeRates();
  }, [fetchExchangeRates]);

  const toggleAddPopup = () => {
    setAddPopupOpen(!isAddPopupOpen);
  };

  const toggleEditPopup = (exchangeRate) => {
    setExchangeRateToEdit(exchangeRate);
    setEditPopupOpen(true);
  };

  const handleOpenDeleteDialog = (id) => {
    setSelectedExchangeRateId(id);
    setDeleteError(null);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedExchangeRateId(null);
    setDeleteError(null);
    setIsDeleting(false);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      await axiosInstance.delete(`/exchange-rate/${selectedExchangeRateId}/`);
      fetchExchangeRates();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error("❌ Eroare la ștergerea ratei de schimb:", error);

      if (
        error.response?.status === 400 &&
        error.response?.data?.error?.includes("referenced in other records")
      ) {
        setDeleteError(
          "This exchange rate cannot be deleted because it is associated with one or more contracts. Please delete or modify the contracts using this exchange rate first."
        );
      } else if (error.response?.status === 409) {
        setDeleteError(
          "This exchange rate is currently in use by existing contracts. You must delete or update those contracts before deleting this exchange rate."
        );
      } else {
        setDeleteError(
          error.response?.data?.error ||
          error.response?.data?.message ||
          "This exchange rate cannot be deleted because it is currently used in other records. Please delete or modify the associated records before attempting to delete this exchange rate."
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddSuccess = () => {
    fetchExchangeRates();
    setAddPopupOpen(false);
  };

  const handleEditSuccess = () => {
    fetchExchangeRates();
    setEditPopupOpen(false);
    setExchangeRateToEdit(null);
  };

  const columns = [
    {
      field: "currency_abbreviation",
      headerName: "Currency",
      flex: 0.9,
      minWidth: 140,
      headerClassName: "bold-header",
      align: "left",
      headerAlign: "left",
    },
    {
      field: "system_currency",
      headerName: "System Currency",
      flex: 0.9,
      minWidth: 140,
      headerClassName: "bold-header",
      align: "left",
      headerAlign: "left",
      renderCell: () => <span>{SYSTEM_CURRENCY}</span>,
    },
    {
      field: "formattedValue",
      headerName: "Conversion to System Currency",
      flex: 1.6,
      minWidth: 250,
      headerClassName: "bold-header",
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        const value = params.row?.rawValue;
        const currencyAbbreviation = params.row?.currency_abbreviation;

        if (
          value === null ||
          value === undefined ||
          value === "" ||
          !currencyAbbreviation
        ) {
          return <span>N/A</span>;
        }

        const numValue = parseFloat(value);
        if (isNaN(numValue)) return <span>N/A</span>;

        const formattedValue = new Intl.NumberFormat("ro-RO", {
          minimumFractionDigits: 4,
          maximumFractionDigits: 6,
        }).format(numValue);

        return (
          <span>{`1 ${currencyAbbreviation} = ${formattedValue} ${SYSTEM_CURRENCY}`}</span>
        );
      },
    },
    {
      field: "formattedDate",
      headerName: "Rate Date",
      flex: 1,
      minWidth: 150,
      headerClassName: "bold-header",
      align: "left",
      headerAlign: "left",
    },
    ...(canEdit || canDelete
      ? [
        {
          field: "actions",
          headerName: "Actions",
          flex: 0.8,
          minWidth: 120,
          headerClassName: "bold-header",
          headerAlign: "center",
          align: "center",
          renderCell: (params) => (
            <Box className="exchange-rate-actions-cell">
              {canEdit && (
                <IconButton
                  onClick={() => toggleEditPopup(params.row)}
                  size="small"
                  color="primary"
                  title="Edit"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {canDelete && (
                <IconButton
                  onClick={() => handleOpenDeleteDialog(params.row.id)}
                  color="error"
                  size="small"
                  title="Delete"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ),
        },
      ]
      : []),
  ];

  if (error) {
    return (
      <div className="exchange-rate">
        <div className="header-container">
          <h2>EXCHANGE RATES </h2>
        </div>
        <Box className="exchange-rate-box-subcontainer">
          <div className="exchange-rate-error-message">{error}</div>
        </Box>
      </div>
    );
  }

  return (
    <div className="exchange-rate">
      <div className="header-container">
        <h2>EXCHANGE RATES </h2>
        <div className="exchange-rate-actions">
          {canCreate && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={toggleAddPopup}
              className="exchange-rate-add-button"
            >
              Add Exchange Rate
            </Button>
          )}
        </div>
      </div>

      <Box className="exchange-rate-box-subcontainer exchange-rate-page exchange-rate-datagrid">
        <div className="exchange-rate-data-grid-container full-width-height custom-exchange-rate-grid">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading} 
            getRowId={(row) => row.id}
            getRowHeight={() => "auto"}
            initialState={{
              sorting: {
                sortModel: [{ field: "id", sort: "desc" }],
              },
              pagination: {
                paginationModel: { pageSize: 12 },
              },
            }}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 12, 20, 25]}
            sx={{
              "& .MuiDataGrid-cell": {
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: "1.4",
                display: "flex",
                alignItems: "center",
                paddingTop: "6px",
                paddingBottom: "6px",
              },
              "& .MuiDataGrid-columnHeaders": {
                whiteSpace: "normal",
              },
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .Mui-selected:hover": {
                backgroundColor: "transparent !important",
              },
            }}
          />
        </div>
      </Box>

      {isAddPopupOpen && (
        <AddExchangeRate
          onClose={() => setAddPopupOpen(false)}
          onSuccess={handleAddSuccess}
          currencies={currencies}
        />
      )}

      {isEditPopupOpen && exchangeRateToEdit && (
        <EditExchangeRate
          exchangeRate={exchangeRateToEdit}
          onClose={() => {
            setEditPopupOpen(false);
            setExchangeRateToEdit(null);
          }}
          onSuccess={handleEditSuccess}
          currencies={currencies}
        />
      )}

      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="confirm-delete-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          id="confirm-delete-dialog-title"
          className={deleteError ? "delete-dialog-title-error" : ""}
        >
          {deleteError ? "Cannot Delete Exchange Rate" : "Confirm Deletion"}
        </DialogTitle>

        <DialogContent>
          {deleteError ? (
            <DialogContentText className="delete-dialog-text-error">
              {deleteError}
            </DialogContentText>
          ) : (
            <DialogContentText>
              Are you sure you want to delete this exchange rate?
              This action cannot be undone.
            </DialogContentText>
          )}
        </DialogContent>

        <DialogActions className="delete-dialog-actions">
          <Button onClick={handleCloseDeleteDialog} color="primary" variant="outlined">
            {deleteError ? "Close" : "Cancel"}
          </Button>

          {!deleteError && (
            <Button
              onClick={handleDelete}
              color="error"
              variant="contained"
              disabled={isDeleting}
              autoFocus
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ExchangeRate;